import { DefaultError } from '@tanstack/query-core'
import { useQuery } from '@tanstack/react-query'
import {
  MOCK_CARD_ENTRIES_RESPONSE,
  MOCK_CARD_FEEDS_ENABLED,
} from 'src/queries/cardFeeds/cardFeeds.mocks'
import {
  CardFeedCard,
  CardFeedEntriesResponse,
  toCardFeedCard,
} from 'src/queries/cardFeeds/cardFeeds.models'
import { fetchCardFeedCards } from 'src/queries/cardFeeds/network/cardFeeds.network'
import {
  cardFeedsQueryKeys,
  queryCacheOptions,
} from 'src/queries/queries.constants'

interface CardFeedCardsResult {
  cards: CardFeedCard[]
  // True when the API reports more than one page; the loaded view is the first
  // page only (real paging is a follow-up — see the table's truncation notice
  // and the design spec).
  isTruncated: boolean
  isLoading: boolean
  isError: boolean
}

interface SelectedCardFeedCards {
  cards: CardFeedCard[]
  isTruncated: boolean
}

const useQueryCardFeedCards = (
  cardFeedId: string | undefined,
  enabled = true,
): CardFeedCardsResult => {
  const query = useQuery<
    CardFeedEntriesResponse,
    DefaultError,
    SelectedCardFeedCards
  >({
    queryKey: cardFeedsQueryKeys.cards(cardFeedId),
    queryFn: async (): Promise<CardFeedEntriesResponse> => {
      if (MOCK_CARD_FEEDS_ENABLED) {
        await new Promise((resolve) => setTimeout(resolve, 300))
        return MOCK_CARD_ENTRIES_RESPONSE
      }
      return fetchCardFeedCards(cardFeedId as string)
    },
    enabled: enabled && cardFeedId !== undefined,
    select: (data) => ({
      cards: data.creditCards.map(toCardFeedCard),
      isTruncated: data.pagination.totalPages > 1,
    }),
    // Assignment state changes outside this page (invite cancellation on the
    // People page, invite expiry, the API's unassign-on-invite-ended
    // listener), so treat the list as always stale and refetch on mount and
    // window focus rather than trusting the 15-minute cache.
    ...queryCacheOptions({
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      staleTime: 0,
    }),
  })

  return {
    cards: query.data?.cards ?? [],
    isTruncated: query.data?.isTruncated ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}

export default useQueryCardFeedCards
